import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import MainLayout from '../../layouts/MainLayout';
import { Card, Tabs, message } from 'antd';
import {
  SettingOutlined,
  AppstoreOutlined,
  TagOutlined,
  CreditCardOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { financeSettingsApi } from '../../utils/api';
import InvoiceGeneralTab from '../../components/finance/InvoiceGeneralTab';
import InvoiceUnitsTab from '../../components/finance/InvoiceUnitsTab';
import InvoicePrefixTab from '../../components/finance/InvoicePrefixTab';
import InvoicePaymentTab from '../../components/finance/InvoicePaymentTab';
import InvoiceTemplateTab from '../../components/finance/InvoiceTemplateTab';
import { TableSkeleton } from '../../components/SkeletonLoaders';

const { TabPane } = Tabs;

const tabKeyToApiTab = {
  general: 'general',
  units: 'units',
  prefix: 'prefix',
  payment: 'payment',
  template: 'template',
};

const availableTabs = Object.keys(tabKeyToApiTab);

const InvoiceSettings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(
    availableTabs.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'general',
  );
  const [loading, setLoading] = useState(false);
  const [tabData, setTabData] = useState({});

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setTabData((prev) => ({ ...prev, [activeTab]: null }));
    setLoading(true);
    try {
      const apiTab = tabKeyToApiTab[activeTab] || activeTab;
      const res = await financeSettingsApi.getInvoiceSettings(apiTab);
      const data = res?.data || res || {};
      setTabData((prev) => ({ ...prev, [activeTab]: data }));
    } catch (error) {
      console.error('خطا در دریافت تنظیمات:', error);
      message.error('خطا در دریافت تنظیمات');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    setSearchParams({ tab: key });
  };

  const currentData = tabData[activeTab] || null;

  return (
    <MainLayout>
      <div style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 text-white flex items-center justify-center shadow-lg">
                <SettingOutlined className="text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">
                  تنظیمات مالی
                </h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm m-0">
                  مدیریت تنظیمات فاکتور، واحدها، پیشوندها و قالب‌ها
                </p>
              </div>
            </div>
          </div>

          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            <Tabs
              activeKey={activeTab}
              onChange={handleTabChange}
              type="card"
              style={{ fontFamily: 'CustomFont' }}
            >
              <TabPane
                tab={<span style={{ fontFamily: 'CustomFont' }}><SettingOutlined className="mr-2" />عمومی</span>}
                key="general"
              >
                {loading && !currentData ? (
                  <TableSkeleton rows={5} columns={3} />
                ) : (
                  <InvoiceGeneralTab data={currentData} onUpdate={loadData} />
                )}
              </TabPane>

              <TabPane
                tab={<span style={{ fontFamily: 'CustomFont' }}><AppstoreOutlined className="mr-2" />واحدها</span>}
                key="units"
              >
                {loading && !currentData ? (
                  <TableSkeleton rows={4} columns={2} />
                ) : (
                  <InvoiceUnitsTab data={currentData} onUpdate={loadData} />
                )}
              </TabPane>

              <TabPane
                tab={<span style={{ fontFamily: 'CustomFont' }}><TagOutlined className="mr-2" />پیشوند</span>}
                key="prefix"
              >
                {loading && !currentData ? (
                  <TableSkeleton rows={5} columns={3} />
                ) : (
                  <InvoicePrefixTab data={currentData} onUpdate={loadData} />
                )}
              </TabPane>

              <TabPane
                tab={<span style={{ fontFamily: 'CustomFont' }}><CreditCardOutlined className="mr-2" />پرداخت</span>}
                key="payment"
              >
                {loading && !currentData ? (
                  <TableSkeleton rows={4} columns={2} />
                ) : (
                  <InvoicePaymentTab data={currentData} onUpdate={loadData} />
                )}
              </TabPane>

              <TabPane
                tab={<span style={{ fontFamily: 'CustomFont' }}><FileTextOutlined className="mr-2" />قالب</span>}
                key="template"
              >
                {loading && !currentData ? (
                  <TableSkeleton rows={3} columns={2} />
                ) : (
                  <InvoiceTemplateTab data={currentData} onUpdate={loadData} />
                )}
              </TabPane>
            </Tabs>
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default InvoiceSettings;
